/**
 * ESCHER FALLS — レベル一覧。MODULE-CONTRACTS §11。
 *
 * ## 面を追加する手順
 * 1. `src/levels/NN-name.json` を**純粋な JSON**（コメント不可）で作る。
 * 2. 下の `import` を1行足す。
 * 3. `RAW_LEVELS` の配列にステージ番号順で足す。
 * 4. `npm run validate-levels` を通す（解のある視点がちょうど1つであること）。
 *
 * それ以外に触る場所は無い。`LEVELS` / `getLevel` は自動で追従する。
 */

import type { Level } from '../core/types';
import { parseLevels } from '../game/levelLoader';

import level01 from './01-first.json';
import level02 from './02-turn.json';
import level03 from './03-fall.json';
import level04 from './04-forest.json';
import level05 from './05-mill.json';
import level06 from './06-falls.json';
import level07 from './07-fork.json';
import level08 from './08-castle.json';
import level09 from './09-rise.json';
import level10 from './10-plan.json';
import level11 from './11-twice.json';
import level12 from './12-ascent.json';
import level13 from './13-lever.json';
import level14 from './14-twohands.json';
import level15 from './15-highhand.json';
import level16 from './16-ladder.json';
import level17 from './17-farjoint.json';
import level18 from './18-windarm.json';
import level19 from './19-twist.json';
import level20 from './20-threehands.json';
import level21 from './21-balance.json';
import extra01 from './x1-updraft.json';
import extra02 from './x2-wheelrise.json';
import extra03 from './x3-arch.json';
import extra04 from './x4-siphon.json';
import extra05 from './x5-twinrise.json';
import extra06 from './x6-twogoals.json';
import extra07 from './x7-share.json';
import extra08 from './x8-highshare.json';
import extra09 from './x9-twinbasin.json';
import extra10 from './x10-summit.json';
import extra11 from './x11-drywell.json';
import extra12 from './x12-unchosen.json';
import extra13 from './x13-twobasins.json';
import extra14 from './x14-silent.json';
import extra15 from './x15-armwell.json';
import extra16 from './x16-riseapart.json';
import extra17 from './x17-invabyss.json';
import extra18 from './x18-highwell.json';
import extra19 from './x19-twistabyss.json';
import extra20 from './x20-endless.json';
import extra21 from './x21-turntable.json';

/**
 * JSON をそのまま import した生の値。ステージ順（1面 → 8面）。
 *
 * JSON import の推論型は `PieceDef` と一致しない（`type` が `string` に広がる）ため、
 * `unknown[]` として公開し、型付けは `parseLevel` に委ねる。`as` で型を偽らないこと。
 */
export const RAW_LEVELS: readonly unknown[] = [
  level01,
  level02,
  level03,
  level04,
  level05,
  level06,
  level07,
  level08,
  // ここから先は仰角（B リング）と真上 (T) を使う。09・10 は各機構の導入なので
  // 意図的に小さく作ってある（新しい仕組みを教える面は易しくする）。
  level09,
  level10,
  level11,
  level12,
  // 第二章。レバーが入り、探索が「どこから見るか」×「どの状態か」の2軸になる。
  level13,
  level14,
  level15,
  // 第二章の続き。レバー＋視点の探索を、関係を一巡する形で深める
  // （A2 A3 → B0 B1 B3 → T）。難易度の軸は手数ではなく**関係の種類**である。
  level16,
  level17,
  level18,
  level19,
  level20,
  level21,
  // エクストラ。id が数字で始まらないものは自動的にエクストラ扱いになる
  // （`game/progression.ts` の categoryOf）。本編を全部通水すると解放される。
  extra01,
  extra02,
  extra03,
  extra04,
  extra05,
  // 其の二。**二つの水盤**（全てに水を届ける）＋レバー＋逆流。
  // 前半5面を全部通すとまとめて開く。
  extra06,
  extra07,
  extra08,
  extra09,
  extra10,
  // 其の二（後半）。**涸れ井戸**——届いてはいけない受け口。
  // これまでの条件はすべて「繋げる」方向だったが、これは初めての負の条件で、
  // 視点を選ぶのではなく**除外する**読みを要求する。
  // 11〜15 は涸れ井戸だけ、16〜20 は逆流とレバーも重なる。
  extra11, extra12, extra13, extra14, extra15,
  extra16, extra17, extra18, extra19, extra20,
  // 回る足場（2026-10-08 試作）。レバーで台ごと水路が 90° 回る、初めての「向き」の仕掛け。
  extra21,
];

/**
 * 本編・エクストラ・二周目を合わせた生の一覧。
 * **`tools/validate-levels.ts` はこれを見る**（二周目を検証から漏らさないため）。
 */
export const ALL_RAW_LEVELS: readonly unknown[] = [...RAW_LEVELS];

/** 検証・正規化済みのレベル。モジュール読み込み時に一度だけ評価する。 */
export const LEVELS: readonly Level[] = parseLevels(ALL_RAW_LEVELS);

const BY_ID: ReadonlyMap<string, Level> = new Map(LEVELS.map((lv) => [lv.id, lv]));

/** id からレベルを引く。見つからなければ `undefined`。 */
export function getLevel(id: string): Level | undefined {
  return BY_ID.get(id);
}
